import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import api from '../lib/api';
import { RecentActivityFeed, type FeedItem } from '../components/RecentActivityFeed';
import { isOrgAdmin } from '../lib/orgRoles';

const ONBOARDING_SEEN_KEY = 'onboarding_seen';

interface UsageInfo {
  inputTokens: number;
  outputTokens: number;
  callCount: number;
}

interface DashboardStats {
  totalSessions: number;
  totalMessages: number;
  enabledPersonas: number;
  usage?: UsageInfo;
}

interface OrgMeResponse {
  questionnaireCompleted?: boolean;
}

export function DashboardPage() {
  const { user } = useAuthContext();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [orgLoading, setOrgLoading] = useState(true);
  const [questionnaireCompleted, setQuestionnaireCompleted] = useState<boolean | null>(null);
  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [feedCursor, setFeedCursor] = useState<string | null>(null);
  const [feedHasMore, setFeedHasMore] = useState(false);
  const [feedLoading, setFeedLoading] = useState(true);

  const loadFeed = useCallback(async (cursor: string | null) => {
    setFeedLoading(true);
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (cursor) params.set('cursor', cursor);
      const res = await api.get(`/practice/feed?${params.toString()}`);
      const data = res.data as { items: FeedItem[]; nextCursor: string | null };
      setFeedItems((prev) => (cursor ? [...prev, ...data.items] : data.items));
      setFeedCursor(data.nextCursor);
      setFeedHasMore(Boolean(data.nextCursor));
    } catch {
      if (!cursor) {
        setFeedItems([]);
        setFeedHasMore(false);
      }
    } finally {
      setFeedLoading(false);
    }
  }, []);

  useEffect(() => {
    api
      .get('/orgs/me')
      .then((res) => {
        const data = res.data as OrgMeResponse;
        setQuestionnaireCompleted(data.questionnaireCompleted ?? true);
      })
      .catch(() => {
        setQuestionnaireCompleted(true);
      })
      .finally(() => setOrgLoading(false));

    api
      .get('/practice/stats')
      .then((res) => setStats(res.data))
      .catch(() => {});

    loadFeed(null);
  }, [loadFeed]);

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  const canManageOrg = isOrgAdmin(user?.orgRole);
  const onboardingSeen = sessionStorage.getItem(ONBOARDING_SEEN_KEY) === '1';

  if (questionnaireCompleted === false && canManageOrg && !onboardingSeen) {
    sessionStorage.setItem(ONBOARDING_SEEN_KEY, '1');
    return <Navigate to="/onboarding" replace />;
  }

  if (questionnaireCompleted === false && !canManageOrg) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="max-w-md w-full bg-amber-50 border border-amber-200 p-8 rounded-lg text-center">
          <div className="text-4xl mb-4">&#x1F6A7;</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Setup in Progress</h2>
          <p className="text-gray-600">
            Your organization&apos;s business profile has not been completed yet. Please contact
            your organization owner or admin to finish setup before you can start using the
            platform.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back{user?.name ? `, ${user.name}` : ''}.</p>
        </div>
        <Link
          to="/practice/new"
          className="hidden md:inline-flex shrink-0 items-center justify-center bg-green-700 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-green-800 transition-colors"
        >
          Start Practice Session
        </Link>
      </div>

      {questionnaireCompleted === false && canManageOrg && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-900">Complete your onboarding</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Complete your business profile so we can tailor practice sessions to your business.
            </p>
          </div>
          <Link
            to="/onboarding"
            className="shrink-0 bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-800 transition-colors text-center"
          >
            Continue Setup
          </Link>
        </div>
      )}

      {questionnaireCompleted === true && canManageOrg && (
        <div className="mb-6 bg-white border border-slate-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-900">Business profile</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              When your positioning or ideal customer profile (ICP) changes, update your answers and
              regenerate AI personas.
            </p>
          </div>
          <Link
            to="/onboarding"
            className="inline-flex justify-center shrink-0 bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-800 transition-colors"
          >
            Update business profile
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Link
          to="/practice"
          className="bg-white p-6 rounded-lg border shadow-sm block text-left no-underline text-inherit hover:shadow-md hover:border-green-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2 transition-shadow"
          aria-label="View practice session history"
        >
          <div className="text-3xl font-bold text-green-700">{stats?.totalSessions ?? '—'}</div>
          <div className="text-sm text-gray-500 mt-1">Practice Sessions (At Bats)</div>
        </Link>
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <div className="text-3xl font-bold text-green-700">{stats?.totalMessages ?? '—'}</div>
          <div className="text-sm text-gray-500 mt-1">Messages Exchanged</div>
        </div>
        <Link
          to="/personas"
          className="bg-white p-6 rounded-lg border shadow-sm block text-left no-underline text-inherit hover:shadow-md hover:border-green-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2 transition-shadow"
          aria-label="View personas"
        >
          <div className="text-3xl font-bold text-green-700">{stats?.enabledPersonas ?? '—'}</div>
          <div className="text-sm text-gray-500 mt-1">Active Personas</div>
        </Link>
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <div className="text-3xl font-bold text-green-700">
            {stats?.usage
              ? stats.usage.inputTokens + stats.usage.outputTokens >= 1000
                ? `${((stats.usage.inputTokens + stats.usage.outputTokens) / 1000).toFixed(1)}k`
                : stats.usage.inputTokens + stats.usage.outputTokens
              : '—'}
          </div>
          <div className="text-sm text-gray-500 mt-1">Your AI tokens (this org)</div>
        </div>
      </div>

      <section
        className="bg-white rounded-lg border mb-8 overflow-hidden"
        aria-label="Recent activity"
      >
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Recent activity</h2>
          <p className="text-xs text-gray-500">Practice sessions across your organization</p>
        </div>
        <RecentActivityFeed
          items={feedItems}
          loading={feedLoading}
          hasMore={feedHasMore}
          onLoadMore={() => loadFeed(feedCursor)}
          emptyLabel="No practice sessions yet."
        />
      </section>
    </div>
  );
}
